import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { BookA, Loader2, Search, Tag, ArrowRight } from "lucide-react";
import { getGlossary, searchTopics } from "../../lib/knowledgeApi";
import { useKnowledge } from "../../lib/knowledge.jsx";
import { Eyebrow, StatusIcon } from "./shared";
import { SearchBox } from "./TopicTree";

function TopicHit({ h }) {
  const { isDone, areaOf } = useKnowledge();
  return (
    <Link to={`/learn/${h.slug}`} data-testid={`kc-hit-${h.slug}`} className="block tile p-4 hover:border-primary/50 transition-colors group">
      <div className="text-[10.5px] uppercase tracking-widest text-muted">{areaOf(h.area)?.title} · {h.group}</div>
      <div className="mt-1 flex items-center gap-2 text-[15px] font-semibold group-hover:text-primary transition-colors"><StatusIcon done={isDone(h.slug)} size={13} />{h.title}</div>
      <p className="text-[12.5px] text-muted mt-1 leading-relaxed">{h.snippet || h.summary}</p>
    </Link>
  );
}

function Terms({ terms }) {
  if (!terms.length) return null;
  return (
    <div data-testid="kc-hit-terms" className="tile p-4">
      <div className="kpi-label mb-2 flex items-center gap-1.5"><BookA size={11} /> Glossary</div>
      <dl className="space-y-2 text-[13px]">
        {terms.map(g => <div key={g.term}><dt className="inline font-semibold">{g.term}</dt> <dd className="inline text-muted">— {g.definition} <Link to={`/learn/${g.topic}`} className="text-primary whitespace-nowrap">Read →</Link></dd></div>)}
      </dl>
    </div>
  );
}

function useSearch(q, tag) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    let alive = true;
    setRes(null);
    searchTopics(q, tag).then(r => alive && setRes(r)).catch(e => alive && setRes({ error: e.message, topics: [], terms: [] }));
    return () => { alive = false; };
  }, [q, tag]);
  return res;
}

function Results({ res, empty }) {
  if (!res) return <div className="py-10 text-muted text-[13px]"><Loader2 size={14} className="inline animate-spin mr-2" />Searching…</div>;
  if (res.error) return <div className="text-critical text-[13px]">{res.error}</div>;
  return (
    <div className="space-y-3">
      <div data-testid="kc-hit-count" className="text-[12px] text-muted font-mono-tabular">{res.topics.length} topic{res.topics.length === 1 ? "" : "s"}{res.terms.length ? ` · ${res.terms.length} glossary terms` : ""}</div>
      {res.topics.length === 0 && res.terms.length === 0 && <div data-testid="kc-hit-empty" className="tile p-6 text-[13px] text-muted">{empty}</div>}
      <Terms terms={res.terms} />
      {res.topics.map(h => <TopicHit key={h.slug} h={h} />)}
    </div>
  );
}

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const res = useSearch(q);
  return (
    <div data-testid="kc-search-page" className="max-w-3xl space-y-5">
      <div><Eyebrow>Search</Eyebrow><h1 className="font-display text-3xl font-semibold tracking-tight mt-2 inline-flex items-center gap-2"><Search size={22} className="text-primary" />“{q}”</h1></div>
      <SearchBox testid="kc-search-page-box" className="h-10" />
      <Results res={res} empty="No topic mentions that yet. Try a broader word, a tag such as “safety stock”, or browse the glossary." />
    </div>
  );
}

export function TagPage() {
  const { tag } = useParams();
  const res = useSearch("", tag);
  return (
    <div data-testid="kc-tag-page" className="max-w-3xl space-y-5">
      <div><Eyebrow>Concept tag</Eyebrow><h1 className="font-display text-3xl font-semibold tracking-tight mt-2 inline-flex items-center gap-2"><Tag size={20} className="text-primary" />{tag}</h1></div>
      <Results res={res} empty="No topics carry this tag." />
    </div>
  );
}

export function GlossaryPage() {
  const [terms, setTerms] = useState(null);
  const [f, setF] = useState("");
  useEffect(() => { getGlossary().then(d => setTerms(d.terms)).catch(() => setTerms([])); }, []);
  const groups = useMemo(() => {
    const out = {};
    (terms || []).filter(g => !f || `${g.term} ${g.definition}`.toLowerCase().includes(f.toLowerCase()))
      .forEach(g => { const k = /[a-z]/i.test(g.term[0]) ? g.term[0].toUpperCase() : "#"; (out[k] ||= []).push(g); });
    return Object.entries(out);
  }, [terms, f]);
  const { cards } = useKnowledge();
  return (
    <div data-testid="kc-glossary" className="max-w-3xl space-y-5">
      <div><Eyebrow>Glossary</Eyebrow><h1 className="font-display text-4xl font-semibold tracking-tight mt-2">Platform terms</h1>
        <p className="text-[14px] text-muted mt-2">{terms ? terms.length : "…"} terms. Each one links to the topic that explains it.</p></div>
      <input data-testid="kc-glossary-filter" value={f} onChange={e => setF(e.target.value)} placeholder="Filter terms…"
             className="w-full h-10 px-3 bg-surface-2 border border-border rounded-sm text-[13px] outline-none focus:border-primary/60" />
      {!terms && <Loader2 size={14} className="animate-spin text-muted" />}
      {groups.map(([k, list]) => (
        <section key={k}>
          <div className="font-display text-2xl font-semibold text-primary mb-2">{k}</div>
          <dl className="divide-y divide-border border-y border-border">
            {list.map(g => (
              <div key={g.term} data-testid={`kc-term-${g.term.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="py-3 grid grid-cols-1 sm:grid-cols-[13rem_1fr] gap-x-6 gap-y-1">
                <dt className="font-semibold text-[14px]">{g.term}</dt>
                <dd className="text-[13.5px] text-fg/85 leading-relaxed">{g.definition}
                  <Link to={`/learn/${g.topic}`} className="ml-2 inline-flex items-center gap-1 text-[12px] text-primary font-medium whitespace-nowrap hover:gap-1.5 transition-[gap]">{cards[g.topic]?.title || "Read"} <ArrowRight size={11} /></Link>
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
